import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Briefcase, UserCheck, Search, FileText, CheckCircle2, XCircle, Eye, EyeOff } from "lucide-react";
import GoogleLoginButton from "@/components/GoogleLoginButton";

// ── Validation helpers ─────────────────────────────────────────────────
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validateEmail(v) {
  if (!v) return "Email is required";
  if (!EMAIL_RE.test(v)) return "Enter a valid email address";
  return "";
}

function validateMobile(v) {
  if (!v) return ""; // optional field — blank is fine
  if (!/^\d+$/.test(v)) return "Only digits allowed";
  if (v.length !== 10) return "Must be exactly 10 digits";
  return "";
}

// Small inline feedback line
function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <p className="flex items-center gap-1 text-xs text-red-600 mt-1">
      <XCircle className="w-3 h-3 shrink-0" />{msg}
    </p>
  );
}

export default function Register() {
  const { register, formatError } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("job_seeker");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mobile, setMobile] = useState("");
  const [busy, setBusy] = useState(false);
  const [consents, setConsents] = useState({});
  const [accepted, setAccepted] = useState(false);
  const [showConsent, setShowConsent] = useState(false);

  // touched state — only show errors after the user has left a field
  const [touched, setTouched] = useState({ email: false, mobile: false });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    api.get("/consents").then(r => setConsents(r.data || {}));
  }, []);

  useEffect(() => { setAccepted(false); }, [role]);

  const emailError  = validateEmail(email);
  const mobileError = validateMobile(mobile);
  const formInvalid = !!emailError || !!mobileError;

  // Mobile: strip any non-digit character on every keystroke
  const handleMobileChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
    setMobile(digits);
  };

  const submit = async (e) => {
    e.preventDefault();
    // Force-touch both fields so errors show
    setTouched({ email: true, mobile: true });
    if (formInvalid) return;
    if (!accepted) {
      toast.error("Please accept the consent terms before signing up");
      return;
    }
    setBusy(true);
    try {
      const u = await register({ name, email, password, role, mobile, consent_accepted: true });
      toast.success("Account created!");
      navigate(u.role === "job_poster" ? "/dashboard/poster" : "/dashboard/seeker");
    } catch (err) {
      toast.error(formatError(err));
    } finally { setBusy(false); }
  };

  const consent = consents[role] || { title: "Terms", body: "" };

  return (
    <div className="min-h-[80vh] grid place-items-center px-4 py-12 bg-white" data-testid="register-page">
      <div className="w-full max-w-xl bg-white border border-black rounded-2xl p-8 shadow-sm">

        {/* Header */}
        <h1 className="text-2xl font-bold text-black">Create your account</h1>
        <p className="text-slate-500 text-sm mt-1">Free for everyone — choose your goal below.</p>

        {/* Role selector */}
        <div className="grid grid-cols-2 gap-3 mt-6">
          <button
            type="button"
            onClick={() => setRole("job_seeker")}
            data-testid="role-seeker"
            className={`text-left p-4 rounded-xl border-2 transition ${
              role === "job_seeker"
                ? "border-black bg-black text-white"
                : "border-slate-300 hover:border-black text-black"
            }`}
          >
            <Search className={`w-5 h-5 ${role === "job_seeker" ? "text-white" : "text-black"}`} />
            <div className="font-semibold mt-2">I'm looking for a job</div>
            <div className={`text-xs mt-0.5 ${role === "job_seeker" ? "text-slate-300" : "text-slate-500"}`}>
              Build profile, apply to jobs
            </div>
          </button>

          <button
            type="button"
            onClick={() => setRole("job_poster")}
            data-testid="role-poster"
            className={`text-left p-4 rounded-xl border-2 transition ${
              role === "job_poster"
                ? "border-black bg-black text-white"
                : "border-slate-300 hover:border-black text-black"
            }`}
          >
            <UserCheck className={`w-5 h-5 ${role === "job_poster" ? "text-white" : "text-black"}`} />
            <div className="font-semibold mt-2">I want to post a job</div>
            <div className={`text-xs mt-0.5 ${role === "job_poster" ? "text-slate-300" : "text-slate-500"}`}>
              Hire from your city for free
            </div>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="space-y-4 mt-6">
          <div>
            <Label className="text-black font-medium">
              {role === "job_poster" ? "Company Name" : "Full Name"}
            </Label>
            <Input
              required
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={role === "job_poster" ? "Your company name" : ""}
              className="mt-1 border-slate-300 focus:border-black focus:ring-black rounded-lg"
              data-testid="reg-name-input"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Email */}
            <div>
              <Label className="text-black font-medium">Email</Label>
              <div className="relative mt-1">
                <Input
                  type="text"
                  inputMode="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value.trim())}
                  onBlur={() => setTouched(t => ({ ...t, email: true }))}
                  className={`pr-8 border rounded-lg transition ${
                    touched.email && emailError
                      ? "border-red-400 focus:border-red-500 focus:ring-red-500"
                      : touched.email && !emailError
                      ? "border-green-500 focus:border-green-500 focus:ring-green-500"
                      : "border-slate-300 focus:border-black focus:ring-black"
                  }`}
                  data-testid="reg-email-input"
                />
                {touched.email && (
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                    {emailError
                      ? <XCircle className="w-4 h-4 text-red-500" />
                      : <CheckCircle2 className="w-4 h-4 text-green-500" />
                    }
                  </span>
                )}
              </div>
              {touched.email && <FieldError msg={emailError} />}
            </div>

            {/* Mobile */}
            <div>
              <Label className="text-black font-medium">
                Phone Number
              </Label>
              <div className="relative mt-1">
                <Input
                  type="text"
                  inputMode="numeric"
                  value={mobile}
                  onChange={handleMobileChange}
                  onBlur={() => setTouched(t => ({ ...t, mobile: true }))}
                  placeholder="10-digit number"
                  maxLength={10}
                  className={`pr-8 border rounded-lg transition ${
                    touched.mobile && mobileError
                      ? "border-red-400 focus:border-red-500 focus:ring-red-500"
                      : touched.mobile && mobile && !mobileError
                      ? "border-green-500 focus:border-green-500 focus:ring-green-500"
                      : "border-slate-300 focus:border-black focus:ring-black"
                  }`}
                  data-testid="reg-mobile-input"
                />
                {/* digit counter */}
                {mobile.length > 0 && (
                  <span className={`absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-medium pointer-events-none ${
                    mobile.length === 10 ? "text-green-600" : "text-slate-400"
                  }`}>
                    {mobile.length}/10
                  </span>
                )}
              </div>
              {touched.mobile && <FieldError msg={mobileError} />}
              {!touched.mobile && (
                <p className="text-[10px] text-slate-400 mt-1">Digits only, exactly 10 if provided</p>
              )}
            </div>
          </div>

          <div>
            <Label className="text-black font-medium">Password</Label>
            <div className="relative mt-1">
              <Input
                type={showPassword ? "text" : "password"}
                required
                minLength={6}
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="border-slate-300 focus:border-black focus:ring-black rounded-lg pr-9"
                data-testid="reg-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(s => !s)}
                tabIndex={-1}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-black"
                data-testid="reg-password-toggle"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Consent block */}
          <div
            className={`rounded-xl border-2 p-4 transition ${
              accepted ? "border-black bg-slate-50" : "border-slate-200 bg-white"
            }`}
            data-testid="consent-block"
          >
            <div className="flex items-start gap-3">
              <Checkbox
                id="consent"
                checked={accepted}
                onCheckedChange={(v) => setAccepted(Boolean(v))}
                className="mt-1 border-slate-400 data-[state=checked]:bg-black data-[state=checked]:border-black"
                data-testid="consent-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="consent" className="cursor-pointer font-semibold text-sm text-black">
                  I accept the {consent.title || "consent terms"}
                </Label>
                <p className="text-xs text-slate-500 mt-1">
                  {role === "job_poster"
                    ? "Includes pricing, 50-day job validity, and applicant data usage."
                    : "Includes data use, profile sharing with employers, and notifications."}
                </p>
                <button
                  type="button"
                  onClick={() => setShowConsent(true)}
                  className="text-xs text-black font-medium hover:underline mt-1 flex items-center gap-1"
                  data-testid="view-consent-btn"
                >
                  <FileText className="w-3 h-3" /> Read full terms
                </button>
              </div>
            </div>
          </div>

          <Button
            type="submit"
            disabled={busy || !accepted}
            className="w-full bg-black hover:bg-slate-800 text-white font-semibold rounded-lg disabled:opacity-40"
            data-testid="reg-submit-btn"
          >
            {busy ? "Creating..." : "Create Account"}
          </Button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-xs text-slate-400 uppercase tracking-wider">or</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        {/* Google login */}
        <GoogleLoginButton role={role} />
        <p className="text-xs text-slate-500 mt-3 text-center">
          By using Google sign-in you also agree to the {role === "job_poster" ? "Employer" : "Job Seeker"} consent terms.
        </p>

        <p className="text-sm text-slate-500 mt-6 text-center">
          Already have an account?{" "}
          <Link to="/login" className="text-black font-semibold hover:underline">Sign in</Link>
        </p>
      </div>

      {/* Consent modal */}
      <Dialog open={showConsent} onOpenChange={setShowConsent}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col border border-black rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-black" data-testid="consent-modal-title">
              {consent.title}
            </DialogTitle>
          </DialogHeader>
          <div
            className="overflow-y-auto text-sm text-slate-700 whitespace-pre-wrap leading-relaxed pr-2"
            data-testid="consent-body"
          >
            {consent.body}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConsent(false)}
              className="border-black text-black hover:bg-slate-100"
            >
              Close
            </Button>
            <Button
              onClick={() => { setAccepted(true); setShowConsent(false); }}
              className="bg-black hover:bg-slate-800 text-white"
              data-testid="accept-from-modal-btn"
            >
              I Accept
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}